"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRightIcon } from "../icons";
import AnimatedIcon from "../ui/animated-icon";
import {
  CodeIcon,
  TargetIcon,
  DeliveryIcon,
  NetworkIcon,
  UpgradeIcon,
  CloudIcon,
  DevOpsIcon,
  VRHeadsetIcon,
  IoTIcon,
  MarketResearchIcon,
  CustomerExperienceIcon,
  DigitalEnterpriseIcon,
  UXConsultingIcon,
  NearshoreIcon,
  AISolutionsIcon,
  DataScienceIcon,
  DataEngineeringIcon,
  MLOpsIcon,
  ArtificialIntelligenceIcon,
  IntelligentAutomationIcon,
  LLMOpsIcon,
} from "../icons";
import {
  menuSlide,
  staggerContainer,
  staggerItem,
  cardHover,
  iconHover,
  fadeInLeft,
  fadeInRight,
  fadeInDown,
} from "../../lib/animations";
import { MegaMenuCategory, modalDataMap } from "../../data/site-data";
import CustomModal from "../modal/modal";
import { MdArrowOutward } from "react-icons/md";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    categories: MegaMenuCategory[];
  };
  title: string;
  modalType?: string;
  showSidebar?: boolean;
}

// Icon mapping function
export const getIconComponent = (iconName: string) => {
  const iconMap: {
    [key: string]: React.ComponentType<{ className?: string }>;
  } = {
    CodeIcon,
    TargetIcon,
    DeliveryIcon,
    NetworkIcon,
    UpgradeIcon,
    CloudIcon,
    DevOpsIcon,
    VRHeadsetIcon,
    IoTIcon,
    MarketResearchIcon,
    CustomerExperienceIcon,
    DigitalEnterpriseIcon,
    UXConsultingIcon,
    NearshoreIcon,
    AISolutionsIcon,
    DataScienceIcon,
    DataEngineeringIcon,
    MLOpsIcon,
    ArtificialIntelligenceIcon,
    IntelligentAutomationIcon,
    LLMOpsIcon,
  };

  return iconMap[iconName] || CodeIcon; // Default fallback
};

export default function MegaMenu({
  isOpen,
  onClose,
  data,
  title,
  showSidebar = true,
  modalType = "",
}: MegaMenuProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(
    modalType === "portfolio" ? data.categories[1] : data.categories[0]
  );
  const [selectedModalData, setSelectedModalData] = useState(
    modalDataMap["default"]
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        event.target instanceof Node &&
        !modalRef.current.contains(event.target)
      ) {
        // onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={menuSlide}
        ref={modalRef}
      >
        <motion.div
          className="absolute inset-0"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
        <motion.div
          className="absolute top-16 lg:h-auto h-full lg:pb-0 pb-20 lg:overflow-y-hidden overflow-y-auto left-0 right-0 bg-white shadow-lg border-t border-gray-200"
          variants={fadeInDown}
        >
          <div className="max-w-7xl mx-auto ">
            <div className="lg:flex">
              {/* Sidebar */}
              {showSidebar && (
                <motion.div
                  className="lg:w-80 w-full bg-gray-900 text-white"
                  variants={fadeInLeft}
                >
                  <div className="p-6">
                    <motion.h3
                      className="text-lg font-semibold mb-4"
                      variants={staggerItem}
                    >
                      {title}
                    </motion.h3>
                    <motion.div
                      className="space-y-1"
                      variants={staggerContainer}
                    >
                      {data.categories.map((category, index) => {
                        if (
                          modalType === "portfolio" &&
                          category.title === "Case Study"
                        ) {
                          return;
                        } else if (
                          modalType === "caseStudy" &&
                          category.title !== "Case Study"
                        ) {
                          return;
                        } else {
                          return (
                            <motion.button
                              key={category.id}
                              onClick={() => setActiveCategory(category)}
                              onMouseEnter={() => setActiveCategory(category)}
                              className={`w-full text-left p-3 rounded-lg transition-colors ${
                                activeCategory.id === category.id
                                  ? "bg-gray-700 text-white"
                                  : "text-gray-300 hover:bg-gray-800 hover:text-white"
                              }`}
                              variants={staggerItem}
                              custom={index}
                              whileHover={{ x: 5 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="font-medium">
                                {category.title}
                              </div>
                              <div className="text-sm text-gray-400 mt-1 lg:block hidden">
                                {category.description}
                              </div>
                            </motion.button>
                          );
                        }
                      })}
                    </motion.div>
                  </div>
                </motion.div>
              )}

              {/* Content */}
              <motion.div
                className={`${
                  showSidebar ? "flex-1" : "w-full"
                } lg:h-auto !h-full lg:overflow-y-hidden overflow-y-auto p-6`}
                variants={fadeInRight}
              >
                <motion.div
                  className={`grid gap-6 ${
                    title !== "Industries"
                      ? "lg:grid-cols-3 grid-cols-1"
                      : "lg:grid-cols-5 grid-cols-1"
                  }`}
                  variants={staggerContainer}
                >
                  {activeCategory.items.map((item, index) => {
                    const IconComponent = item.icon
                      ? getIconComponent(item.icon)
                      : null;

                    return (
                      <motion.div
                        key={item.id}
                        className="group cursor-pointer"
                        variants={staggerItem}
                        custom={index}
                        whileHover="hover"
                        initial="initial"
                      >
                        <motion.div
                          className="p-4 rounded-lg group-hover:text-primary border border-gray-200 hover:border-primary hover:shadow-md transition-all h-full"
                          variants={cardHover}
                          onClick={() => {
                            if (title === "Industries") return;
                            const modalKey = item.id || "default";
                            setSelectedModalData(
                              modalDataMap[modalKey] || modalDataMap["default"]
                            );
                            setIsModalOpen(true);
                          }}
                        >
                          {item.title && (
                            <div className="flex items-center">
                              {IconComponent && (
                                <motion.div
                                  className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3"
                                  whileHover={{ scale: 1.1, rotate: 5 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <AnimatedIcon className="w-5 h-5 text-white flex items-center justify-center">
                                    {/* <IconComponent /> */}
                                    {index + 1}
                                  </AnimatedIcon>
                                </motion.div>
                              )}
                              <span className="flex  justify-between w-full">
                                <h4 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                                  {item.title}
                                </h4>
                                {title !== "Industries" && (
                                  <MdArrowOutward className=" invisible group-hover:visible " />
                                )}
                              </span>
                            </div>
                          )}

                          {item.description && title !== "Industries" && (
                            <p className="text-sm text-gray-600 my-3">
                              {item.description}
                            </p>
                          )}

                          {item.buttonText && (
                            <motion.button
                              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              transition={{ duration: 0.2 }}
                            >
                              {item.buttonText}
                              <motion.div
                                whileHover={{ x: 3 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ArrowRightIcon className="w-4 h-4" />
                              </motion.div>
                            </motion.button>
                          )}

                          {item.subServices && item.subServices.length > 0 && (
                            <ul className="text-sm text-gray-600 space-y-1">
                              {item.subServices.map((service, serviceIndex) => (
                                <motion.li
                                  key={serviceIndex}
                                  className="flex items-center hover:text-primary transition-colors"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    delay: serviceIndex * 0.1,
                                  }}
                                  whileHover={{ x: 5 }}
                                >
                                  <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                                  <a href="#" className="hover:underline">
                                    {service}
                                  </a>
                                </motion.li>
                              ))}
                            </ul>
                          )}
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            </div>
          </div>
          <CustomModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            data={selectedModalData}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
